import type { Order } from 'types/orders';
import type { UpcomingAction } from 'store/slices/upcoming-actions/upcoming-actions.slice';
import type { AdditionalFile } from 'components/Admin/organisms/CarEditOrAdd/types';

export enum Categories {
  ELEGANT = 'elegant',
  SPORT = 'sport',
  SUPER_SPORT = 'super sport',
  EXCLUSIVE = 'exclusive',
}

export enum CarStatus {
  ACTIVE = 'ACTIVE',
  DRAFT = 'DRAFT',
  ARCHIVE = 'ARCHIVE',
  DELETED = 'DELETED',
}

export interface InitialState {
  carDocumentPendingStatus: Record<
    'registrationDoc' | 'insuranceDoc' | 'insuranceInformation',
    boolean
  >;
  carCreationProgress: number | null;
  items: Array<Car>;
  searchResult: Array<Car>;
  isPending: boolean;
  selectedCar: Car | null;
  newCar: Partial<CarRequest>;
  carChangedKeys: Array<keyof Car>;
  totalCount: number;
  mediaUploadProgress: number;
  filesBeingDeleted: Array<string>;
}

export enum CarCharacteristicsType {
  ACCELERATION = 'ACCELERATION',
  ENGINE = 'ENGINE',
  POWER = 'POWER',
  TRUNK = 'TRUNK',
  TRANSMISSION = 'TRANSMISSION',
  MAX_SPEED = 'MAX_SPEED',
  SEATS_QUANTITY = 'SEATS_QUANTITY',
  LIFT_SYSTEM = 'LIFT_SYSTEM',
}

interface TitleValue {
  title: string;
  value: string | number;
}

interface CarCharacteristics extends TitleValue {
  type: CarCharacteristicsType;
}

export interface Condition extends TitleValue {
  type: ConditionType;
}

export enum EcoDonationType {
  CO2 = 'CO2',
  TREES_QUANTITY = 'TREES_QUANTITY',
  POLLUTION = 'POLLUTION',
}

export enum CarOptionType {
  REDUCED_DEPOSIT = 'REDUCED_DEPOSIT',
  STORAGE = 'STORAGE',
  LUGGAGE = 'LUGGAGE',
  CAR_PRESENTATION = 'CAR_PRESENTATION',
  DELIVERY = 'DELIVERY',
  CANCELATION_INSURANCE = 'CANCELATION_INSURANCE',
}
export type CarOption = {
  title: string;
  type: CarOptionType;
  value?: number;
};
export enum ConditionType {
  DRIVER_MINIMUM_AGE = 'DRIVER_MINIMUM_AGE',
  DRIVER_LICENCE_AGE = 'DRIVER_LICENCE_AGE',
}

export interface AdditionalPrices {
  deposit: number;
  reducedDeposit: number;
  extraHourPrice: number;
  extraKmPrice: number;
  forRefuelling: number;
  extraFeeIfDepositReduced: number;
}

export interface CarPrices extends AdditionalPrices {
  perAboveWeek: number;
  perWeekendDay: number;
  perRegularDay: number;
}

export type IncludedKmForRent = Record<
  keyof Omit<CarPrices, keyof AdditionalPrices>,
  number
>;

export type CarDocument = {
  title: string;
  file: File | null;
  id: string;
  key: 'registrationDoc' | 'insuranceDoc' | 'insuranceInformation';
};

export interface FilesDto {
  id: string;
  title: string;
  mediaUrl: string;
  carId: string;
  isAdditionalFile?: boolean;
  isDocument?: boolean;
  isInitialPhoto?: boolean;
  isVideoGuide?: boolean;
  updatedAt?: Date;
}

export interface Car {
  id: string;
  category: Categories;
  registrationDate: Date;
  title: string;
  description: string;
  shortDescription: string;
  generalThumbnail: FilesDto | null;
  prices: CarPrices;
  numberOfTrees: number;
  co2Emissions?: string;
  characteristics: Array<CarCharacteristics>;
  conditions?: Array<Condition>;
  options: Array<CarOption>;
  status: CarStatus;
  pickupAndReturnLocations: Array<string>;
  currentOrderId?: string;
  rentableOnDate?: Date;
  currentOrder: Order;
  orders: Array<Order>;
  includedKmForRentDate: Record<
    keyof Omit<CarPrices, keyof AdditionalPrices>,
    number
  >;
  onlyWithSubscription: boolean;
  currentMileage: number;
  unavailabilityDates: Array<Date>;
  blockedDates?: Array<Date>;
  isRentable: boolean;
  upcomingActions?: Array<UpcomingAction>;
  additionalFiles?: Array<AdditionalFile>;
  parkingAddress: string;
  gpsLocation: string;
  uploadedFiles: Array<FilesDto>;
  registrationDoc: File | FilesDto | null;
  insuranceDoc: File | FilesDto | null;
  insuranceInformation: File | FilesDto | null;
}

export interface CarRequest
  extends Omit<
    Car,
    | 'id'
    | 'uploadedFiles'
    | 'insuranceDoc'
    | 'registrationDoc'
    | 'insuranceInformation'
  > {
  images: Array<AdditionalFile>;
  insuranceDoc: File | null;
  insuranceInformation: File | null;
  videoGuide: File;
  registrationDoc: File | null;
  initialCarPhotos: Array<{ id: string; file: File }>;
}

export interface GetCarsParams {
  status: string;
}

export interface SearchCarsParam {
  dateRange: string;
  maxPrice: number;
  category: string;
}
